import React, { useEffect, useState } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
// import MDAlert from "components/MDAlert";
import MDProgress from "components/MDProgress";
import MDSnackbar from "components/MDSnackbar";

import {
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { setEditDoc, setListRefresh } from "features/documents/documentSlice";

function DocumentList() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const dispatch = useDispatch();
  const selectedVehicle = useSelector(
    (state) => state.document.selectedVehicle
  );
  const listRefresh = useSelector((state) => state.document.listRefresh);

  const fetchDocuments = async () => {
    if (!selectedVehicle) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No session token found. Please log in first.");
        setLoading(false);
        return;
      }
      const url = `https://v3.dhundhoo.com/vehicles/download/${selectedVehicle}`;
      const response = await axios.get(url, {
        headers: {
          "x-dhundhoo-session": token,
          Accept: "application/json, text/plain, */*",
        },
      });
      setDocuments(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [selectedVehicle, listRefresh]);

  const handleDeleteClick = (docId) => {
    setSelectedDocId(docId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDocId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No session token found. Please log in first.");
        setLoading(false);
        return;
      }
      await axios.post(
        `https://v3.dhundhoo.com/vehicles/deleteDocument?documentId=${selectedDocId}`,
        {
          headers: {
            "x-dhundhoo-session": token,
          },
        }
      );
      setDocuments(documents.filter((doc) => doc.documentId !== selectedDocId));
      handleCloseDialog();
      dispatch(setListRefresh());
      setSnackbar({
        open: true,
        message: "Document deleted successfully!",
        color: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || err.message,
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (doc) => {
    dispatch(setEditDoc(doc));
  };

  return (
    <MDBox
      // mt={2}
      sx={{
        padding: 2,
        paddingLeft: "24px",
        display: "flex",
        gap: "1rem",
        flexWrap: "wrap",
        flexDirection: "column",
      }}
    >
      {loading && <MDProgress variant="indeterminate" />}
      {/* {error && (
        <MDAlert color="error" sx={{}}>
          {"No Entry"}
        </MDAlert>
      )} */}

      <MDBox>
        {!loading && !error && selectedVehicle && (
          <MDTypography sx={{ my: 2, padding: 1 }} variant="h6">
            Recently Uploaded Documents
          </MDTypography>
        )}
      </MDBox>

      <MDBox
        sx={{
          display: "flex",
          gap: "1rem",
          flexWrap: "wrap",
          maxHeight: "310px", 
          overflowY: "auto", 
          overflowX: "hidden",
          pr: 1, 
          "&::-webkit-scrollbar": {
            width: "6px", 
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1", 
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1", 
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#c1c1c1 #f1f1f1",
        }}
      >
        {!loading &&
          !error &&
          documents.map((doc, index) => (
            <Card
              key={index}
              sx={{
                mb: 1,
                boxShadow: null,
                display: "flex",
                alignItems: "center",
                padding: 1,
                width: "300px",
                border: "1px solid #f3f2f2ff",
              }}
            >
              <CardContent sx={{ flexGrow: 1, width: "100%", padding: 1 }}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div>
                    <MDTypography variant="h6" color="dark">
                      Document Name
                    </MDTypography>
                    <MDTypography variant="body2">
                      {doc.documentName}
                    </MDTypography>
                    <MDTypography variant="h6" color="dark">
                      Document Expiry Date
                    </MDTypography>
                    <MDTypography variant="body2">
                      {doc.documentExpireDate}
                    </MDTypography>
                  </div>
                </div>
              </CardContent>
              <MDBox sx={{ width: "100%", display: "flex" }}>
                <IconButton
                  sx={{ color: "#1976d2" }}
                  onClick={() => window.open(doc.s3Url)}
                >
                  <VisibilityIcon />
                </IconButton>
                <IconButton
                  sx={{ color: "#4caf50" }}
                  onClick={() => handleEdit(doc)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteClick(doc.id)}
                  sx={{ color: "#f44336" }}
                >
                  <DeleteIcon />
                </IconButton>
              </MDBox>
            </Card>
          ))}
      </MDBox>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{"Confirm Delete"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this document? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="secondary">
            Cancel
          </MDButton>
          <MDButton onClick={handleConfirmDelete} color="error" autoFocus>
            OK
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Snackbar for success/error messages */}
      <MDSnackbar
        color={snackbar.color}
        icon="notifications"
        title={snackbar.color === "success" ? "Success" : "Error"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        close
      />
    </MDBox>
  );
}

export default DocumentList;
